import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { PositionProps } from 'styled-system';

import { useMatchesViewport } from '../../../hooks/media';
import type { SuggestionsListContainerProps } from '../types';

interface UseGetCenterProps extends Pick<
  SuggestionsListContainerProps,
  'inputContainerRef' | 'isSingleColumn'
> {
  listRef: MutableRefObject<HTMLDivElement>;
  open: boolean;
}

interface CalculateLeftMargin {
  containerWidth: number;
  inputWidth: number;
  inputX: number;
  matchesDesktop: boolean;
}

interface GetLeftMargin extends Omit<UseGetCenterProps, 'open'> {
  matchesDesktop: boolean;
}

const defaultStyle: PositionProps = { position: 'absolute' };

const calculateLeftMargin = ({
  containerWidth,
  inputWidth,
  inputX,
  matchesDesktop,
}: CalculateLeftMargin) => {
  if (matchesDesktop) {
    return window.innerWidth / 2 - inputX - containerWidth / 2;
  }
  const widthDiff = (inputWidth - containerWidth) / 2;

  return inputWidth > containerWidth ? -widthDiff : widthDiff;
};

const getLeftMargin = ({
  inputContainerRef,
  listRef,
  matchesDesktop,
  isSingleColumn,
}: GetLeftMargin) => {
  if (!inputContainerRef.current || !listRef.current || (isSingleColumn && matchesDesktop)) {
    return 0;
  }
  const { width: containerWidth } = listRef.current.parentElement.getBoundingClientRect();
  const { x: inputX, width: inputWidth } = inputContainerRef.current.getBoundingClientRect();

  return calculateLeftMargin({
    containerWidth,
    inputWidth,
    inputX,
    matchesDesktop,
  });
};

const useGetCenterProps = ({
  inputContainerRef,
  listRef,
  open,
  isSingleColumn,
}: UseGetCenterProps) => {
  const {
    matches: { matchesDesktop, matchesTablet },
  } = useMatchesViewport();
  const [marginLeft, setMarginLeft] = useState(0);

  const handleResize = useCallback(() => {
    if (open) {
      setMarginLeft(
        getLeftMargin({
          inputContainerRef,
          listRef,
          matchesDesktop,
          isSingleColumn,
        })
      );
    }
  }, [inputContainerRef, listRef, matchesDesktop, open, isSingleColumn]);

  useEffect(() => {
    handleResize();
  }, [handleResize, open]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize, matchesDesktop]);

  return matchesTablet && open && { ...defaultStyle, ml: `${marginLeft}px` };
};

export default useGetCenterProps;
